// ============================================================================
// English (en) — canonical key set for DONiTSH!T
//
// Every other locale (fr/es/it/de/nl/ar/zh/ko/ru/sw) mirrors this structure.
// Missing keys fall back to en via i18next `fallbackLng`. The goal is
// near-zero English leakage when switching to any other language, so add
// the translated string in EVERY locale file whenever you add a new key.
// ============================================================================
const en = {
  // ----------------------------------------------------------------- nav
  nav: {
    home: "Home",
    hub: "Wellness Hub",
    dosha: "Dosha Test",
    mens: "Men's Wellness",
    womens: "Women's Wellness",
    families: "Families & Parenting",
    teens: "Teens & Young Adults",
    relationships: "Relationships",
    workshops: "Workshops & Corporate",
    marketplace: "Marketplace",
    shop: "Shop",
    coach: "Ask Donny",
    ask_donny: "Ask Donny",
    support: "Support",
    login: "Log in",
    signup: "Sign up",
    profile: "Profile",
    logout: "Log out",
    my_business: "My Business",
    list_your_business: "List your business",
    overview: "Overview",
    wellness: "Wellness",
    wellness_mens: "Men's Wellness",
    wellness_womens: "Women's Wellness",
    wellness_mental: "Mental Wellness",
    wellness_nutrition: "Nutrition",
    wellness_sleep: "Sleep",
    wellness_sports: "Sports Performance",
    rel_luv: "Luv Connect",
    rel_couples: "Couples Connect",
    rel_healthy: "Healthy Relationships",
    rel_communication: "Communication",
    rel_dating: "Dating",
    rel_wellness: "Relationship Wellness",
    fam_parenting: "Parenting",
    fam_wellness: "Family Wellness",
    fam_teens: "Teens & Youth",
    business: "Business & Corporate",
    biz_partners: "Partner Businesses",
    biz_corporate: "Corporate Wellness",
    biz_offers: "Special Offers",
    biz_marketplace: "Marketplace",
    biz_directory: "Business Directory",
    admin: "Admin",
    account: "Account",
    sso: "SSO",
    language: "Language",
    brand_home_aria: "DONiTSH!T home",
    brand_eyebrow: "Wellness",
    brand_eyebrow_long: "Wellness Ecosystem",
  },

  // ----------------------------------------------------------------- hero (legacy)
  hero: {
    eyebrow: "— A PREMIUM WELLNESS ECOSYSTEM",
    title_line_1_prefix: "Discover your ",
    title_line_1_accent: "dosha.",
    title_line_2_prefix: "Build your ",
    title_line_2_accent: "structure.",
    title_line_3_prefix: "Transform your ",
    title_line_3_accent: "wellbeing.",
    subtitle: "Wellness, relationships, families, and business — woven into one luxury ecosystem grounded in dosha science and self-coaching.",
    cta_dosha: "Take the Dosha Test",
    cta_wellness: "Explore Wellness",
    cta_app: "Download the App",
    cta_hub: "Explore the Wellness Hub",
    descriptors: ["Wellness", "Nutrition", "Fitness", "Mental Wellness", "Men's Health", "Women's Health", "Relationships", "Corporate Wellness", "AI Coaching", "Dosha Insights"],
    trust: ["Science-informed", "Ayurveda-aware", "Multilingual", "Private by design"],
  },

  // ----------------------------------------------------------------- home
  home: {
    // Member benefits strip
    member_benefits_eyebrow: "— Member Benefits",
    member_benefits_title: "Curated offers from partner businesses",
    member_benefits_subtitle: "Exclusive discounts and trials from vetted businesses across the DONiTSH!T ecosystem. Free to join — sign in to reveal the codes.",
    member_benefits_all: "View all offers",
    member_only: "Member-only — free to join",

    // Hero
    hero_eyebrow: "— A PREMIUM WELLNESS ECOSYSTEM",
    hero_l1_pre: "Discover your ",
    hero_l1_acc: "dosha.",
    hero_l2_pre: "Build your ",
    hero_l2_acc: "structure.",
    hero_l3_pre: "Transform your ",
    hero_l3_acc: "wellbeing.",
    hero_subtitle: "Wellness, relationships, families, and business — woven into one luxury ecosystem grounded in dosha science and self-coaching.",
    hero_cta_dosha: "Take the Dosha Test",
    hero_cta_wellness: "Explore Wellness",
    hero_alt: "DONiTSH!T — Be Your Own Coach. A cosmic wellness ecosystem.",

    // Gateways
    gateways_eyebrow: "— The Ecosystem",
    gateways_title: "Five gateways. One ecosystem.",
    gateways_subtitle: "Each gateway is a content-rich destination — articles, expert resources, recommended practitioners, and tools.",
    gateway_enter: "Enter",
    gateway_wellness: "Wellness",
    gateway_wellness_blurb: "Mind, body, and rhythm. Six pillars — Men's, Women's, Mental, Nutrition, Sleep, and Sports Performance.",
    gateway_wellness_eyebrow: "01 · The Ecosystem",
    gateway_relationships: "Relationships",
    gateway_relationships_blurb: "Luv Connect, Couples Connect, and the science of healthy connection — dating, communication, and relationship wellness.",
    gateway_relationships_eyebrow: "02 · The Ecosystem",
    gateway_families: "Families & Parenting",
    gateway_families_blurb: "Parenting, family wellness, and teens & youth — rhythms and rituals that make family life a wellness practice.",
    gateway_families_eyebrow: "03 · The Ecosystem",
    gateway_business: "Business & Corporate",
    gateway_business_blurb: "Partner Businesses, Corporate Wellness, Special Offers, the Marketplace, and the full Business Directory.",
    gateway_business_eyebrow: "04 · The Ecosystem",
    gateway_shop: "Shop",
    gateway_shop_blurb: "Digital programs, multi-week courses, and curated physical goods via Shopify.",
    gateway_shop_eyebrow: "05 · The Ecosystem",

    // Ask Donny tile
    ask_donny_eyebrow: "— 06 · The Assistant",
    ask_donny_title: "Meet Donny, your AI assistant",
    ask_donny_h: "Donny is the AI assistant for the entire DONiTSH!T ecosystem.",
    ask_donny_body: "Get personalized starting points, reflection prompts, and curated next steps across wellness, relationships, family, and business. Donny doesn't replace your inner coach — it sharpens it.",
    ask_donny_avatar_alt: "Donny — your DONiTSH!T AI assistant",
    ask_donny_avatar_eyebrow: "Your AI Assistant",
    ask_donny_meta_chip: "AI · Educational · Multilingual",

    // Motivation
    motivation_eyebrow: "— Daily Prompts",
    motivation_title: "Click any thought. Take one small action.",
    motivation_subtitle: "Every chip opens a single reflection and a clear next step. No decoration — only direction.",

    // Dosha strip
    dosha_eyebrow: "— 07 · The Compass",
    dosha_title: "Your wellness blueprint, in six minutes",
    dosha_body: "The Dosha Test maps your unique mind-body constitution and personalizes everything you see across the ecosystem — practitioners, content, and Donny's prompts.",

    // Motivation chip data (used in HomePage). 8 chips, each with key/label/quote/cta_label/cta_to.
    chips: [
      { key: "wellness", label: "Wellness", quote: "Wellness is not the absence of illness — it's the presence of vitality.", cta_label: "Explore Wellness", cta_to: "/wellness" },
      { key: "balance", label: "Balance", quote: "Balance is a verb. You don't find it — you practice it, every day.", cta_label: "Take the Dosha Test", cta_to: "/dosha" },
      { key: "transformation", label: "Transformation", quote: "Real transformation is slow, quiet, and deeply personal. Trust the process.", cta_label: "Ask Donny", cta_to: "/ask-donny" },
      { key: "performance", label: "Performance", quote: "Performance without recovery is just a slow form of burnout.", cta_label: "Sports Performance", cta_to: "/wellness/sports" },
      { key: "nutrition", label: "Nutrition", quote: "Food is your most-frequently-taken supplement. Make it count.", cta_label: "Nutrition", cta_to: "/wellness/nutrition" },
      { key: "lifestyle", label: "Lifestyle", quote: "Your lifestyle is the architecture of your future self.", cta_label: "Mental Wellness", cta_to: "/wellness/mental" },
      { key: "connection", label: "Connection", quote: "The strongest predictor of long-term wellbeing is the quality of your relationships.", cta_label: "Relationships", cta_to: "/relationships" },
      { key: "family", label: "Family", quote: "A family is the first ecosystem any child ever knows.", cta_label: "Families & Parenting", cta_to: "/families" },
    ],
   welcome_back: "Welcome back, {{name}}", welcome_admin_sub: "You’re signed in as an admin. Your console is one click away.", welcome_member_sub: "Continue exploring your DONiTSH!T journey.", welcome_cta_admin: "Admin Console", welcome_cta_profile: "My Profile" },

  // ----------------------------------------------------------------- sections
  sections: {
    categories: "Wellness Categories",
    categories_sub: "Find the right path across the DONiTSH!T ecosystem — from mental wellness to men's and women's health, nutrition, performance, and beyond.",
    articles: "Featured Articles",
    articles_sub: "Editorial guides written by our wellness team — for the realities of modern life.",
    programs: "Featured Programs",
    programs_sub: "Guided, multi-week experiences with measurable outcomes — grounded in science and Ayurvedic wisdom.",
    app: "Download the DONiTSH!T App",
    app_sub: "Your pocket coach for daily wellness rituals, personalized protocols, and on-the-go guidance — available on iOS and Android.",
    coach: "Ask Donny",
    coach_sub: "A multilingual AI companion trained on the DONiTSH!T wellness library. Educational guidance — never a diagnosis, always in your language.",
    community: "Community Impact",
    community_sub: "We believe wellness is a right, not a luxury — and we build for everyone, in every language.",
  },

  // ----------------------------------------------------------------- common
  common: {
    read_more: "Read more",
    read: "Read",
    explore: "Explore",
    start: "Start",
    next: "Next",
    back: "Back",
    submit: "Submit",
    loading: "Loading\u2026",
    select: "Select",
    learn_more: "Learn more",
    minutes: "min",
    save: "Save",
    open: "Open",
    close: "Close",
    yes: "Yes",
    no: "No",
    cancel: "Cancel",
    confirm: "Confirm",
    edit: "Edit",
    delete: "Delete",
    sign_in: "Sign in",
    error: "Something went wrong",
    success: "Success",
    saved: "Saved",
    could_not_save: "Could not save",
    sending: "Sending\u2026",
    saving: "Saving\u2026",
    submitting: "Submitting\u2026",
    refresh: "Refresh",
    reason: "Reason",
    settings: "Settings",
    country: "Country",
    required_fields: "Please fill all required fields",
    something_wrong: "Something went wrong. Please try again.",
    not_found: "Not found",
    view_all: "View all",
  },

  // ----------------------------------------------------------------- dosha
  dosha: {
    title: "The Dosha Test",
    eyebrow: "— The Dosha Test",
    intro: "Discover your biotype — a lens for how you eat, train, sleep, and recover. Personalized in minutes, drawn from Ayurvedic wisdom and modern body-science.",
    step1_title: "How do you identify?",
    gender_male: "Male",
    gender_female: "Female",
    gender_nb: "Prefer not to say",
    step2_title: "What brings you here today?",
    step2_hint: "Choose one to three wellness goals.",
    goal_label: "Goal",
    goals: {
      energy: "Energy", sleep: "Sleep", stress: "Stress", fitness: "Fitness",
      nutrition: "Nutrition", confidence: "Confidence", mental: "Mental wellness",
      hormones: "Hormonal balance", focus: "Focus", performance: "Performance",
    },
    selected_count: "{{n}}/3 selected",
    step3_title: "A few personalized questions",
    question_label: "Question {{n}}",
    progress_aria: "progress",
    fun_fact: "Did you know?",
    result_title: "Your Dosha Profile",
    your_dosha: "Your Dosha",
    modern_body_type: "Modern body type",
    strengths: "Strengths",
    challenges: "Challenges",
    foods: "Recommended foods",
    avoid: "Foods to ease up on",
    fitness: "Fitness approach",
    wellness_recs: "Wellness recommendations",
    affirmation: "Your affirmation",
    quote: "",
    share: "Share my result",
    save_to_profile: "Save to my profile",
    cta_short: "Take the Dosha Test",
    cta_floating: "Take the Dosha Test",
    share_title: "My Dosha Result — DONiTSH!T",
    link_copied: "Link copied",
    no_result: "No result",
    related_reads: "Related reads",
    practitioners_eyebrow: "— Aligned With You",
    practitioners_title: "Practitioners recommended for your {{type}} energy",
    practitioners_subtitle: "Coaches, therapists, and studios whose approach complements your constitution.",
  },

  // ----------------------------------------------------------------- coach (Ask Donny full page)
  coach: {
    title: "Ask Donny",
    subtitle: "Educational guidance from your AI assistant for the DONiTSH!T ecosystem — never a substitute for a licensed clinician.",
    placeholder: "Ask Donny anything: energy, sleep, stress, hormones, relationships\u2026",
    send: "Send",
    recs: "Recommended for you",
    crisis_note: "If you are in crisis, please contact your local emergency line listed below.",
    disclaimer: "Donny provides educational wellness guidance and is not a substitute for licensed medical advice.",
    sample_prompts: [
      "I'm a 38 year-old man feeling burned out. Where do I start?",
      "I'm in perimenopause and not sleeping well. What helps?",
      "How do I support my teen with body-image stress?",
    ],
    start_with: "Start with",
    country_optional: "Country (for crisis resources, optional)",
    recommendations_appear: "Recommendations appear after you ask a question.",
    sources_label: "From the DONiTSH!T library",
    articles_label: "Articles",
    ebooks_label: "eBooks",
    workouts_label: "Workouts",
    workshops_label: "Workshops",
    crisis_support: "Crisis Support",
    error_again: "Coach is taking longer than usual. Please try again.",
    real_coach_eyebrow: "— Ready for the next step?",
    real_coach_title: "Consider connecting with a real practitioner",
    real_coach_subtitle: "When you're ready to go deeper than self-coaching, here are vetted practitioners from our Wellness Marketplace.",
    real_coach_crisis_subtitle: "If you'd like ongoing human support, these licensed professionals in our Wellness Marketplace can help.",
  },

  // ----------------------------------------------------------------- ask_donny (concierge drawer)
  ask_donny: {
    title: "Ask Donny",
    persona: "Your DONiTSH!T AI assistant",
    eyebrow: "AI · Educational · Multilingual",
    ask_about: "Ask Donny about this",
    intro: "I'm Donny — your concierge across the DONiTSH!T ecosystem. I see you're on",
    intro_2: "Ask me anything related to it — or pick a starter below.",
    starters_label: "Suggested starters",
    placeholder: "Ask Donny about this page\u2026",
    context_label: "Context",
    context_chip: "Page-aware",
    open_fullpage: "Open full page →",
    disclaimer: "Educational guidance only — Donny doesn't replace medical, mental-health, or professional advice.",
    aria_bubble: "Ask Donny — about {{label}}",
    aria_close: "Close Donny",
    aria_send: "Send",
    bubble_concierge: "Your AI concierge",
    bubble_ask_about: "Ask about {{label}}",
    error_again: "Donny is taking a moment. Please try again.",
    crisis_inline: "Donny suggests connecting with a real professional — see resources below.",
    recs: {
      articles: "Articles",
      ebooks: "eBooks",
      workouts: "Workouts",
      workshops: "Workshops",
      member_benefits: "Member Benefits",
      crisis_support: "Crisis Support",
    },
    // Map of page topic -> human label (used in drawer header + starter selection)
    context_labels: {
      "mens-wellness": "Men's Wellness",
      "womens-wellness": "Women's Wellness",
      "mental-wellness": "Mental Wellness",
      "nutrition": "Nutrition",
      "sleep": "Sleep",
      "sports-performance": "Sports Performance",
      "wellness": "Wellness",
      "luv-connect": "Luv Connect",
      "couples-connect": "Couples Connect",
      "healthy-relationships": "Healthy Relationships",
      "communication": "Communication",
      "dating": "Dating",
      "relationship-wellness": "Relationship Wellness",
      "relationships": "Relationships",
      "parenting": "Parenting",
      "family-wellness": "Family Wellness",
      "teens-youth": "Teens & Youth",
      "families-parenting": "Families & Parenting",
      "partner-businesses": "Partner Businesses",
      "corporate-wellness": "Corporate Wellness",
      "special-offers": "Special Offers",
      "business-directory": "Business Directory",
      "business": "Business & Corporate",
      "marketplace": "Wellness Marketplace",
      "shop": "DONiTSH!T Shop",
      "dosha": "Dosha Test",
      "article": "Wellness Article",
      "profile": "Profile",
      "support": "Support",
      "ask-donny": "Ask Donny",
      "ecosystem": "Homepage",
      "default": "DONiTSH!T",
    },
    // Starter prompts keyed by topic. "default" fallback used when no match.
    starters: {
      "mens-wellness": [
        "What habits boost energy and recovery for men?",
        "How do I balance work, family, and fitness?",
        "What's a good morning routine for men?",
      ],
      "womens-wellness": [
        "How do hormones affect my energy through the month?",
        "What rituals support women's mental wellbeing?",
        "Help me build a sustainable self-care routine.",
      ],
      "mental-wellness": [
        "How do I manage chronic stress without medication?",
        "What's the difference between burnout and anxiety?",
        "Suggest a 5-minute reset I can do at my desk.",
      ],
      "nutrition": [
        "How do I eat for my dosha?",
        "What's a balanced day on a plate?",
        "Quick anti-inflammatory meal ideas?",
      ],
      "sleep": [
        "How do I fix a broken sleep schedule?",
        "What's a wind-down routine that actually works?",
        "Tell me about sleep and the doshas.",
      ],
      "sports-performance": [
        "How should I structure recovery for high training loads?",
        "What's smart hydration for endurance?",
        "Talk to me about overtraining warning signs.",
      ],
      "relationships": [
        "How do I communicate boundaries with my partner?",
        "What are signs of a healthy relationship?",
        "I want to reconnect with my partner — where do I start?",
      ],
      "couples-connect": [
        "Give us a weekend ritual that deepens connection.",
        "How do we keep intimacy alive long-term?",
        "Tips for emotional safety in conversation?",
      ],
      "luv-connect": [
        "How do I show up for myself before dating?",
        "What's a healthy way to start a new relationship?",
        "Talk to me about attachment styles.",
      ],
      "parenting": [
        "How do I stay calm during meltdowns?",
        "Tips for raising emotionally aware kids.",
        "Help me build a screen-time policy that works.",
      ],
      "family-wellness": [
        "Give us a family wellness Sunday plan.",
        "How do we make wellbeing a shared family habit?",
        "Suggest meaningful, screen-free family rituals.",
      ],
      "teens-youth": [
        "How do I support a teen with anxiety?",
        "Talk to me about social media and self-esteem.",
        "Healthy habits for student-athletes.",
      ],
      "business": [
        "What does a great corporate wellness program look like?",
        "How can my company benefit from the DONiTSH!T marketplace?",
        "Tips for leading a healthier team.",
      ],
      "shop": [
        "Which program fits a beginner like me?",
        "Recommend a digital course based on my dosha.",
        "What's the difference between Vata, Pitta, and Kapha programs?",
      ],
      "marketplace": [
        "Find me a coach near me.",
        "Which practitioners help with anxiety and burnout?",
        "How are practitioners vetted?",
      ],
      "dosha": [
        "Walk me through what a dosha is.",
        "How accurate is this test?",
        "What do I do once I get my result?",
      ],
      "ecosystem": [
        "Give me a 3-step starting point inside DONiTSH!T.",
        "What's the best place to begin if I'm new?",
        "How does DONiTSH!T work as an ecosystem?",
      ],
      "default": [
        "Where should I start in the DONiTSH!T ecosystem?",
        "How can I take a small step today?",
        "Recommend something based on my dosha.",
      ],
    },
  },

  // ----------------------------------------------------------------- shop
  shop: {
    title: "Shop",
    eyebrow: "— The Shop",
    subtitle: "Premium digital products, multi-week programs, and physical goods curated through our Shopify storefront.",
    digital: "Digital",
    physical: "Physical",
    personalised: "Personalised",
    all: "All",
    coming_soon: "Available",
    buy: "Buy",
    checkout: "Checkout",
    checkout_on_shopify: "Buy on Shopify",
    checkout_failed: "Checkout failed",
    success_title: "Thank you — your purchase is being confirmed.",
    cancel_title: "Checkout cancelled.",
    confirming: "Confirming payment\u2026",
    payment_confirmed: "Payment confirmed",
    still_confirming: "Still confirming. We'll email you once Stripe confirms.",
    could_not_check: "Could not check status. If charged, our team will reach out.",
    back_to_shop: "Back to shop",
    home: "Home",
    physical_pending_sync: "Live Shopify catalog is connecting. Showing curated samples until your first sync completes.",
    physical_coming_soon: "Physical commerce launches once Shopify is connected.",
    physical_empty_title: "Physical products are launching soon",
    physical_empty_synced: "Our Shopify catalog is connected and currently syncing — fresh inventory will appear here shortly.",
    physical_empty_unconfigured: "We're finalizing our supply partners. Subscribe to the newsletter to be notified when our shelves open.",
    // Phase 8 — availability chips + coming-soon + personalised
    state_buy_now: "Buy now",
    state_request: "Request",
    state_dropship: "Dropship",
    state_coming_soon: "Coming soon",
    dropship_note: "partner-fulfilled",
    image_pending: "Image coming soon",
    on_request: "On request",
    request_quote_cta: "Request a quote",
    tab_empty_title: "Products arriving soon",
    tab_empty_sub: "We're finalising this catalogue. Explore the other tabs while we prepare it.",
    personalised_empty_title: "Personalised orders coming soon",
    personalised_empty_sub: "Bespoke and made-to-order products will appear here.",
    personalised_eyebrow: "— Made to order",
    personalised_cta_title: "Bespoke, bulk or event orders.",
    personalised_cta_sub: "Send us your brief and we'll come back with a tailored quote and lead time.",
    coming_soon_eyebrow: "— Arriving in the ecosystem",
    coming_soon_title: "New categories, released as we curate them.",
    coming_soon_sub: "Join the waitlist below and we'll email you the moment a category opens.",
    waitlist_eyebrow: "Join the waitlist",
    waitlist_email_ph: "your@email.com",
    waitlist_cta: "Notify me",
    waitlist_sent: "You're on the waitlist.",
    waitlist_sent_hint: "We'll email you when {{cat}} launches.",
    waitlist_failed: "Could not join the waitlist.",
    // Phase 8 — quote request page
    quote_eyebrow: "— Personalised · Quote request",
    quote_title: "Request a personalised quote",
    quote_title_for: "Request a quote — {{name}}",
    quote_subtitle: "Made-to-order, bulk and event products are handled individually. Send us a brief and we'll come back with a tailored quote and lead time.",
    quote_field_name: "Your full name",
    quote_field_email: "Email address",
    quote_field_phone: "Phone (optional)",
    quote_field_company: "Company / event",
    quote_field_quantity: "Quantity (e.g. 200, 500, 1000)",
    quote_field_timing: "Delivery timing (e.g. by 15 October)",
    quote_field_format: "Format / product details (size, flavour, style)",
    quote_field_personalisation: "Personalisation requirements (colours, message, moulds)",
    quote_field_logo: "Logo / branding (describe, or send by email after submitting)",
    quote_field_notes: "Anything else we should know?",
    quote_submit: "Send request",
    quote_sent: "Thanks — our team will reply within one business day.",
    quote_failed: "Could not submit your quote request.",
    quote_sent_title: "Your request is on its way.",
    quote_sent_sub: "A member of the DONiTSH!T team will reply to your inbox within one business day.",
    quote_sent_head: "Request received",
    quote_sent_body: "We've logged your brief and shared it with our production team. If you don't see a reply, please check your spam folder or contact us via the Support page.",
    quote_contact_support: "Contact Support",
    quote_reference_label: "Reference",
    quote_sidebar_eyebrow: "How it works",
    quote_step_1: "Send us your brief — quantity, format and personalisation.",
    quote_step_2: "We reply with a tailored quote and production timeline.",
    quote_step_3: "We produce and ship — DONiTSH!T style, from start to finish.",
    quote_sidebar_note: "Prices vary by quantity, personalisation and lead time.",
    quote_legal_note: "By submitting you agree to be contacted about your enquiry. See our Privacy Policy for details.",
  },

  // ----------------------------------------------------------------- support
  support: {
    title: "Support",
    eyebrow: "— Support",
    subtitle: "Frequently asked questions, direct contact, and mental health resources around the world.",
    faq: "Frequently asked questions",
    contact: "Contact us",
    mh_title: "Mental Health Resources",
    mh_sub: "Choose your country to see local resources. If you are in immediate danger, please call your local emergency number.",
    crisis_eyebrow: "— Crisis Support",
    country_select: "Select your country",
    your_name: "Your full name",
    your_email: "Your email address",
    topic: "Topic",
    message: "Your message",
    submit_contact: "Send message",
    submitted: "Message received. Our team will reply shortly.",
    could_not_send: "Could not send",
    global_resources_label: "Global resources",
    faqs: [
      { q: "Is DONiTSH!T a medical service?", a: "No. We provide educational wellness guidance. Always speak to a licensed clinician for medical decisions." },
      { q: "How does the AI Coach work?", a: "Our coach is grounded on DONiTSH!T's wellness library and is multilingual. It offers educational, science-and-Ayurveda-informed guidance." },
      { q: "Is my data private?", a: "Yes. Your account, conversations, and Dosha results are private. We never sell your data." },
      { q: "Do you ship physical products?", a: "Physical products (apparel + adaptogen teas) are available via our Shopify-powered store." },
      { q: "How can I cancel a digital purchase?", a: "Reach out via the form below within 7 days of purchase if your product hasn't been delivered or accessed." },
    ],
  },

  // ----------------------------------------------------------------- auth
  auth: {
    login_title: "Welcome back",
    login_eyebrow: "— Account",
    login_subtitle: "Welcome back. Sign in to continue exploring your DONiTSH!T ecosystem.",
    signup_title: "Create your account",
    signup_eyebrow: "— Account",
    signup_subtitle: "Create your account and unlock your personalized DONiTSH!T ecosystem.",
    google: "Continue with Google",
    email: "Email address",
    password: "Password",
    name: "Full name",
    forgot: "Forgot your password?",
    have_account: "Already have an account?",
    no_account: "New to DONiTSH!T?",
    sign_in: "Sign in",
    sign_up: "Sign up",
    reset_title: "Reset your password",
    reset_sent: "If that email exists, a reset link has been generated.",
    new_password: "New password",
    set_password: "Set new password",
    welcome_back_toast: "Welcome back",
    welcome_toast: "Welcome to DONiTSH!T",
    login_failed: "Login failed",
    signup_failed: "Signup failed",
    password_min: "Password must be at least 8 characters",
    password_min_short: "Min 8 characters",
    oauth_error: "Google sign-in was cancelled or failed",
    sso_note: "Frictionless sign-in. You'll come back here authenticated.",
    future_sso: "Future-ready: Apple & Facebook will be added.",
    signing_in: "Signing you in\u2026",
    password_updated: "Password updated. Please sign in.",
    password_updated_short: "Password updated. Sign in to continue.",
    reset_token_dev_note: "Reset token generated (V1: shown here for testing).",
    reset_request_failed: "Could not request reset",
    reset_failed: "Could not reset password",
    reset_generate: "Generate reset token",
    reset_token_placeholder: "Reset token",
   welcome_toast_title: "Welcome back, {{name}}", welcome_toast_login: "Glad to see you again.", welcome_toast_register: "Your DONiTSH!T account is ready.", logout_toast_title: "You’ve been signed out", logout_toast_sub: "Come back anytime.", logout_confirm_title: "Sign out?", logout_confirm_message: "You’ll need to sign in again to access your account and admin tools.", logout_confirm_yes: "Sign out", logout_confirm_cancel: "Cancel", legal_ack_prefix: "By continuing, you agree to the", legal_ack_terms: "Terms of Use", legal_ack_conjunction: "and acknowledge the", legal_ack_privacy: "Privacy Policy", legal_ack_suffix: "." },

  // ----------------------------------------------------------------- hub
  hub: {
    title: "Wellness Hub",
    eyebrow: "— Wellness Hub",
    subtitle: "Editorial articles, programs, and recommendations across fourteen wellness categories — built for real life.",
    search_placeholder: "Search articles, topics, programs\u2026",
    all: "All",
    empty: "No articles matched. Try removing your filters.",
    practitioners_for: "Practitioners for {{topic}}",
    practitioners_general: "Featured practitioners across the ecosystem",
    practitioners_subtitle: "Hand-picked therapists, coaches, nutritionists, and studios from the DONiTSH!T Wellness Marketplace.",
    practitioners_marketplace_eyebrow: "— The Marketplace",
    practitioners_find_pro: "— Find a Pro",
  },

  // ----------------------------------------------------------------- mens
  mens: {
    title: "Men's Wellness",
    eyebrow: "— Men",
    subtitle: "Energy, focus, hormonal health, healthy aging — modern, inclusive, and evidence-aware support for men at every stage.",
    pillars: [
      "Energy", "Burnout", "Sleep", "Stress", "Confidence",
      "Hormonal Health & Andropause", "Testosterone Education",
      "Erectile Dysfunction Education", "Healthy Aging",
      "Nutrition for Men", "Fitness & Recovery",
    ],
    pillars_eyebrow: "— The Pillars",
    pillars_title: "Eleven pillars of modern men's wellness",
    editorial_eyebrow: "— Editorial",
    editorial_title: "Articles for men",
    editorial_subtitle: "From burnout to andropause — modern, inclusive, and evidence-aware guidance.",
    cta_dosha: "Take the Dosha Test",
    cta_donny: "Ask Donny",
    practitioners_title: "Find a coach, therapist, or trainer for men",
    practitioners_subtitle: "Practitioners in the DONiTSH!T Marketplace with expertise in men's mental, physical, and hormonal wellness.",
    image_alt: "A confident mature man embracing his wellness journey",
  },

  // ----------------------------------------------------------------- womens
  womens: {
    title: "Women's Wellness",
    eyebrow: "— Women",
    subtitle: "Cycle, perimenopause, postpartum, and beyond — hormonal life-stages explained with clarity, compassion, and modern science.",
    pillars: ["Menopause", "Postpartum", "Hormonal Balance", "Energy", "Mental Wellness", "Nutrition"],
    pillars_eyebrow: "— The Pillars",
    pillars_title: "Hormonal life-stages, with clarity",
    editorial_eyebrow: "— Editorial",
    editorial_title: "Articles for women",
    editorial_subtitle: "From cycle to perimenopause to postpartum — evidence-informed, compassionate guidance.",
    cta_dosha: "Take the Dosha Test",
    cta_donny: "Ask Donny",
    practitioners_title: "Find a women's-health practitioner",
    practitioners_subtitle: "Therapists, nutritionists, and coaches specializing in cycle, perimenopause, menopause, and postpartum.",
    image_alt: "Women's wellness",
  },

  // ----------------------------------------------------------------- families
  families: {
    title: "Families & Parenting",
    eyebrow: "— Families",
    subtitle: "Mental wellbeing for families, practical resources for parents, and real support for single parents — because raising a family is its own wellness practice.",
    pillars: ["Mental Wellbeing for Families", "Parent Resources", "Healthy Lifestyle Education", "Single Parents"],
    pillars_eyebrow: "— Family Support",
    pillars_title: "What we focus on",
    editorial_eyebrow: "— Editorial",
    editorial_title: "Articles for families",
    editorial_subtitle: "Practical, science-informed guidance for parents, single parents, and families of every shape.",
    cta_donny: "Ask Donny",
    cta_hub: "Wellness Hub",
    practitioners_title: "Find a family-support practitioner",
    practitioners_subtitle: "Family therapists, parenting coaches, and single-parent specialists in our Wellness Marketplace.",
    image_alt: "Families",
  },

  // ----------------------------------------------------------------- teens
  teens: {
    title: "Teens & Young Adults",
    eyebrow: "— Teens & Young Adults",
    subtitle: "Confidence, body image, sports performance, mental wellbeing, and academic resilience — designed for ages 13 to 24.",
    pillars: ["Confidence", "Body Image", "Sports Performance", "Mental Wellbeing", "Academic Resilience"],
    pillars_eyebrow: "— What we cover",
    pillars_title: "Confidence and resilience",
    editorial_eyebrow: "— Editorial",
    editorial_title: "Articles for teens & young adults",
    editorial_subtitle: "From confidence and body image to performance and academic resilience.",
    cta_dosha: "Take the Dosha Test",
    cta_donny: "Ask Donny",
    practitioners_title: "Find a teen-friendly therapist or coach",
    practitioners_subtitle: "Confidence coaches, family therapists, and youth-focused mental-health pros in our Wellness Marketplace.",
    image_alt: "Teens",
  },

  // ----------------------------------------------------------------- workshops
  workshops: {
    title: "Workshops & Corporate Wellness",
    eyebrow: "— For Teams",
    subtitle: "From wellness essentials to executive-level resilience — we design programs that genuinely move the needle for teams and leaders.",
    services: ["Wellness Workshops", "Nutrition Workshops", "Mental Wellness Workshops", "Team Building", "Burnout Prevention", "Workplace Wellness Programs"],
    services_eyebrow: "— Our Services",
    services_title: "What we offer",
    request_eyebrow: "— Request a Workshop",
    request_title: "Tell us about your team",
    request_subtitle: "Share a few details and we will reply within 48 hours with a tailored proposal.",
    cta: "Request a workshop",
    image_alt: "Corporate wellness",
    topic_default: "Workshop request",
  },

  // ----------------------------------------------------------------- footer
  footer: {
    rights: "All rights reserved.",
    tagline: "Be your own coach.",
    sentence: "DONiTSH!T brings wellness, nutrition, fitness, mental health, men's and women's health, relationships, corporate wellness, AI coaching, and Dosha insights together in one premium experience.",
    legal: "Privacy · Terms · Cookies",
    legal_aria: "Legal",
    legal_privacy: "Privacy Policy",
    legal_terms: "Terms of Use",
    legal_community: "Community Guidelines",
    legal_disclaimer: "Health & Wellness Disclaimer",
    sections: {
      explore: "Explore", learn: "Learn", account: "Account", legal: "Legal",
      wellness: "Wellness", relationships: "Relationships & Families", business: "Business & Shop",
      tools: "Tools & Account",
    },
  },

  // ----------------------------------------------------------------- marketplace
  marketplace: {
    title: "Wellness Marketplace",
    subtitle: "Discover trusted therapists, nutritionists, coaches, studios, retreats, and corporate wellness partners — all in one curated directory.",
    eyebrow: "— Verified Partners",
    all: "All categories",
    search_placeholder: "Search by service, name, or city\u2026",
    country_placeholder: "Country (US, FR\u2026)",
    empty: "No listings matched your filters. Try broadening your search.",
    book: "Request booking",
    inquire: "Send inquiry",
    visit: "Visit website",
    featured_badge: "Featured",
    premium_badge: "Premium",
    featured: "Featured",
    premium: "Premium",
    featured_title: "Featured Practitioners",
    featured_subtitle: "Hand-picked therapists, coaches, nutritionists, and studios from the DONiTSH!T Wellness Marketplace.",
    featured_partner_eyebrow: "— Featured Partner",
    favorite: "Save",
    unfavorite: "Remove from saved",
    added_to_saved: "Saved to your practitioners",
    removed_from_saved: "Removed from saved",
    fav_error: "Could not update saved practitioners",
    login_to_save: "Sign in to save practitioners",
    view_all: "View all",
    no_recs_yet: "Practitioner recommendations will appear here as more local pros join the directory.",
    filtering_by: "Filtering by",
    clear_filter: "Clear",
    services_offered: "Services offered",
    languages: "Languages",
    location: "Location",
    contact: "Contact",
    about: "About",
    business_not_found: "Business not found",
    back_to_marketplace: "Marketplace",
    list_business_title: "List your wellness business",
    list_business_sub: "Reach an engaged, multilingual audience across the DONiTSH!T website and mobile app. Premium tiers unlock featured placement, workshop bookings, and corporate partnerships.",
    list_business_cta: "List your business",
    my_business_title: "My business listings",
    my_business_sub: "Manage the listings you have submitted to the DONiTSH!T Marketplace.",
    my_business_empty: "No listings yet. Start by submitting your first business.",
    view_public_listing: "View public listing",
    register_title: "Submit your business",
    register_sub: "Tell us about your practice. After a brief review, your listing will appear across the DONiTSH!T ecosystem.",
    find_pro_eyebrow: "— Find a Pro",
    find_pro_default: "Find a practitioner",
    submit_failed: "Could not submit",
    could_not_send: "Could not send",
    form: {
      name: "Business name", tagline: "Short tagline", description: "Detailed description",
      category: "Category", services: "Services (comma separated)",
      languages: "Languages offered (comma separated, ISO codes)",
      city: "City", country: "Country code (e.g. US, FR)",
      email: "Contact email", phone: "Contact phone", website: "Website URL",
      image: "Hero image URL (optional)", submit: "Submit for review",
      thanks: "Thank you. Your listing is now pending review.",
    },
    inquiry: {
      title: "Send a message", sub: "Tell the business a bit about what you are looking for. They will reply directly.",
      name: "Your name", email: "Your email", phone: "Your phone (optional)",
      message: "Your message", submit: "Send inquiry", sent: "Your inquiry was sent.",
    },
    booking: {
      title: "Request a booking", sub: "Request a session, consult, or workshop slot. The business will confirm by email.",
      type: "Type", type_session: "Private session", type_consult: "Initial consult", type_workshop: "Workshop",
      when: "Preferred date/time", note: "Note", submit: "Submit booking request", sent: "Your booking request was sent.",
    },
    topic_labels: {
      "mental-health": "Mental health",
      "mens-wellness": "Men's wellness",
      "womens-wellness": "Women's wellness",
      "menopause": "Menopause",
      "family-support": "Family support",
      "teens": "Teens",
      "couples": "Couples",
      "relationships": "Relationships",
      "nutrition": "Nutrition",
      "fitness": "Fitness",
      "sleep": "Sleep",
      "longevity": "Longevity",
      "burnout": "Burnout",
      "corporate": "Corporate wellness",
      "ayurveda": "Ayurveda",
      "yoga": "Yoga",
      "retreat": "Retreats",
    },
  },

  // ----------------------------------------------------------------- relationships
  relationships: {
    title: "Relationship Wellness",
    subtitle: "Communication, self-esteem, healthy boundaries, dating after divorce, and family wellbeing — relationship education with science and compassion.",
    eyebrow: "— Relationship Wellness",
    pillars_eyebrow: "— The Pillars",
    pillars_title: "What we focus on",
    editorial_eyebrow: "— Editorial",
    editorial_title: "Articles on relationships",
    editorial_subtitle: "Conversation, repair, and growth — with science and compassion.",
    featured_eyebrow: "— Featured Partner",
    featured_title: "Luvconnect — Couples Connect",
    luv_cta: "Discover Luvconnect",
    cta_donny: "Ask Donny",
    cta_hub: "Browse the Hub",
    practitioners_eyebrow: "— Couples & Relationship Pros",
    practitioners_title: "Find a relationship coach or couples therapist",
    practitioners_subtitle: "Vetted licensed therapists and certified coaches who specialize in relationships, couples work, and repair.",
    image_alt: "A mature couple sharing a connected moment",
  },

  // ----------------------------------------------------------------- offers (Phase 6.4)
  offers: {
    page_title: "Member Benefits",
    page_subtitle: "Exclusive offers from partner businesses across the DONiTSH!T ecosystem — therapy intros, retreat discounts, program bundles, and more. Sign in (free) to reveal the codes.",
    hero_eyebrow: "— Business & Corporate · Special Offers",
    view_offers: "View offers",
    explore_marketplace: "Explore Marketplace",
    filter_eyebrow: "— Filter",
    filter_title: "Find an offer that fits",
    filter_subtitle: "Browse by category. New offers are rotated in regularly.",
    all: "All",
    featured: "Featured",
    promo_code: "Promo Code",
    reveal_code: "Reveal promo code",
    revealing: "Revealing\u2026",
    sign_in_to_reveal: "Sign in to reveal code",
    member_benefit: "Member-only benefit — free to join.",
    copy_code: "Copy code",
    copy: "Copy",
    copied: "Copied",
    code_revealed: "Code revealed",
    code_copied: "Promo code copied",
    reveal_failed: "Couldn't reveal the code. Please try again.",
    copy_failed: "Couldn't copy. Long-press the code instead.",
    valid_until: "Until",
    terms: "Terms & conditions",
    visit_business: "Visit business",
    empty_title: "No offers in this category yet",
    empty_body: "We rotate a curated set of offers each month. Check back soon, or browse all categories.",
    editorial_eyebrow: "— What we cover",
    editorial_title: "How offers work",
    donny_eyebrow: "— Concierge",
    donny_title: "Ask Donny about offers",
    donny_hl: "Ask Donny to match an offer to what you need.",
    donny_sub: "Tell Donny what you're looking for — therapy, retreats, coaching, corporate — and we'll surface the best active offer.",
    donny_meta_chip: "Member-only · Curated · Rotating",
    business_offers_eyebrow: "Active Member Benefits",
    // Owner UI
    owner_member_benefits: "Member Benefits",
    owner_close: "Close",
    owner_propose: "Propose offer",
    owner_title_ph: "Offer title",
    owner_desc_ph: "Short description",
    owner_discount_ph: "Discount (e.g. 20% OFF)",
    owner_code_ph: "Promo code",
    owner_terms_ph: "Terms (optional)",
    owner_submit: "Submit for review",
    owner_submitting: "Submitting\u2026",
    owner_submitted: "Offer submitted for review",
    owner_submit_failed: "Couldn't submit the offer",
    owner_review_note: "Your offer will be reviewed by an admin before going live.",
    owner_missing_fields: "Please fill the required fields",
    owner_none_yet: "No offers proposed yet.",
    owner_withdraw: "Withdraw",
    owner_withdrawn: "Offer withdrawn",
    owner_confirm_delete: "Withdraw this offer?",
    owner_reason_prefix: "Reason",
  },

  // ----------------------------------------------------------------- admin (Phase 6.4 admin console)
  admin: {
    eyebrow: "— Admin Console",
    eyebrow_short: "— Admin",
    offers_title: "Business Offers",
    offers_subtitle: "Approve, feature, hide, or expire offers proposed by partner businesses.",
    loading: "Loading\u2026",
    no_items: "No offers in this view.",
    not_admin: "Admin access required",
    load_failed: "Couldn't load offers",
    refresh: "Refresh",
    approve: "Approve",
    approved: "Offer approved",
    reapprove: "Re-approve",
    reject: "Reject",
    rejected: "Offer rejected",
    feature: "Feature",
    unfeature: "Unfeature",
    featured: "Offer featured",
    unfeatured: "Removed from featured",
    hide: "Hide",
    hidden: "Offer hidden",
    expire: "Expire",
    expired: "Offer expired",
    edit: "Edit",
    delete: "Delete",
    deleted: "Offer deleted",
    save: "Save",
    saved: "Saved",
    cancel: "Cancel",
    confirm_delete: "Delete this offer permanently?",
    confirm_reject: "Confirm reject",
    reject_reason_placeholder: "Reason (optional, shown to the owner)",
    edit_title_ph: "Title",
    edit_desc_ph: "Description",
    edit_discount_ph: "Discount label (e.g. 20% OFF)",
    edit_code_ph: "Promo code",
    edit_terms_ph: "Terms & conditions",
    signed_in_as: "Signed in as",
    featured_label: "featured",
    biz_label: "biz",
    code_label: "code",
    reveals_label: "reveals",
    until: "until {{date}}",
    no_end: "no end",
    tabs: {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      hidden: "Hidden",
      expired: "Expired",
      all: "All",
    },
    statuses: {
      pending: "pending",
      approved: "approved",
      rejected: "rejected",
      hidden: "hidden",
      expired: "expired",
    },
    shell: { title: "Admin Console", checking: "Checking admin access…", back: "Back", back_to_site: "Back to site", toggle_nav: "Toggle admin navigation", nav_dashboard: "Dashboard", nav_users: "Admin Users", nav_offers: "Offers", nav_content: "Content", nav_import: "Import", nav_kb: "Ask Donny KB", nav_businesses: "Businesses", soon: "Soon" }, guard: { eyebrow: "— Restricted", title: "Admin access required", subtitle: "You don’t have permission to view this page.", go_home: "Go home" }, dashboard: { eyebrow: "— Overview", title: "Admin Dashboard", subtitle: "Quick overview of the DONiTSH!T ecosystem.", stat_db_admins: "DB Admins", stat_env_admins: "ENV Admins", stat_recent_actions: "Recent Actions", shortcut_offers: "Business Offers", shortcut_offers_sub: "Approve, feature, hide, or expire partner offers.", recent_title: "Recent activity", recent_eyebrow: "Audit Log", recent_empty: "No activity yet.", system: "system", roadmap_title: "Content Manager + Ask Donny Knowledge Base", roadmap_sub: "Coming in Phase 7.2–7.4 — Markdown content authoring, document import (PDF/DOCX/MD/TXT), and a retrieval-grounded knowledge base for Donny." }, users: { eyebrow: "— Access Control", title: "Admin Users", subtitle: "Manage who can access the admin console. The ADMIN_EMAILS environment variable always grants access; this list is for day-to-day management.", subtitle_short: "Promote or demote admins from the DB allowlist.", env_title: "Environment allowlist", env_sub: "Configured via the {{var}} environment variable. Read-only here — update via the secrets dashboard.", env_empty: "Empty", add_title: "Promote an admin", add_sub: "Adds the user to the DB allowlist and updates their account if they’re registered.", email_label: "Email", email_placeholder: "user@example.com", notes_label: "Notes", notes_placeholder: "Notes (optional)", add_btn: "Promote", email_invalid: "Enter a valid email", promote_existed: "{{email}} was already an admin", promote_success: "{{email}} promoted to admin", promote_failed: "Couldn’t promote user", demote_env_still: "Removed from DB. {{email}} is still in {{var}}.", demote_success: "{{email}} demoted", demote_failed: "Couldn’t demote user", confirm_demote: "Remove admin access from {{email}}?", remove: "Demote", db_title: "Database allowlist", db_sub: "Day-to-day admins managed via this dashboard.", count_label: "admin(s)", empty: "No admin users in the database yet.", self_chip: "You", also_env: "Also in ENV", added_by: "Added by {{email}}", load_failed: "Couldn’t load admin users" },
   content: { title: "Content Manager", eyebrow: "— Editorial", subtitle: "Create, edit, and publish multilingual articles powering the DONiTSH!T ecosystem. English is required; other languages fall back to English when missing.", new_btn: "New article", import_btn: "Import", new_article: "New article", back_to_list: "Back to list", untitled: "Untitled", search_placeholder: "Search by title, slug, tag…", clear: "Clear", load_failed: "Couldn’t load content", load_one_failed: "Couldn’t load this article", save_failed: "Couldn’t save content", save_draft: "Save draft", publish: "Publish", unpublish: "Unpublish", publish_success: "Published ‘{{slug}}’", save_success: "Saved ‘{{slug}}’", unpublish_success: "Moved to draft", unpublish_failed: "Couldn’t unpublish", confirm_unpublish: "Unpublish this article? It will move to draft.", archive: "Archive", archive_success: "Archived ‘{{slug}}’", archive_failed: "Couldn’t archive content", confirm_archive: "Archive ‘{{slug}}’? You can restore it later.", edit: "Edit", empty_title: "No content yet", empty_sub: "Create your first article to get started.", source_migrated: "Migrated", updated: "Updated", showing: "Showing {{count}} of {{total}}", fallback_note: "English serves as fallback for other locales when empty.", required: "REQ", add_locale: "Add language…", remove_locale: "Remove locale", locale_hint: "This locale is optional. If left empty, viewers will see the English content as fallback.", field_title: "Title", field_summary: "Summary", field_body: "Body (Markdown)", field_slug: "Slug", field_hero: "Hero image", field_topic: "Topic", field_tags: "Tags", title_placeholder_en: "Your headline…", title_placeholder_other: "Optional — leave empty to use English", summary_placeholder: "One or two sentences describing this article…", slug_hint: "Lowercase letters, digits, and hyphens. Used in the public URL.", hero_url_placeholder: "Or paste an image URL…", tag_placeholder: "Add a tag…", add_tag: "Add tag", show_preview: "Show preview", hide_preview: "Hide preview", upload: "Upload", upload_success: "Image uploaded", upload_failed: "Couldn’t upload image", public_url: "Public URL", status: { draft: "Draft", published: "Published", archived: "Archived" }, validation: { en_title: "English title is required.", slug: "Slug is required.", slug_format: "Slug must be lowercase letters, digits, and hyphens.", body: "English body is recommended before publishing." } },
   import: { eyebrow: "— Content Import", title: "Import documents into the ecosystem", subtitle: "Upload PDFs, Word documents, Markdown, or plain text. We’ll extract the body, ask the AI for topic, tags, and a short summary — and create an editable draft you can publish or send to Ask Donny’s knowledge base.", back_to_list: "Back to Content Manager", upload_title: "Upload a document", upload_hint: "Allowed: {{exts}}. Maximum size: {{mb}}MB.", choose_file: "Choose a file to import", click_to_select: "Click anywhere in this box to select", locale_label: "Source language", clear: "Clear", start: "Import & analyze", uploading: "Importing…", upload_success: "Imported “{{title}}” as draft", upload_failed: "Import failed", unsupported_ext: "Unsupported file type: .{{ext}}", too_large: "File too large (max {{mb}}MB)", meta_load_failed: "Couldn’t load import taxonomy", imported_eyebrow: "— Draft created", chars: "chars", show_preview: "Show preview", hide_preview: "Hide preview", no_body: "No body extracted.", ai_title: "AI suggestions", ai_ok: "AI", ai_fallback: "Fallback", ai_help: "Edit the topic, tags, and summary before publishing. AI suggestions are a starting point, not a final decision.", topic_label: "Ecosystem topic", topic_none: "— No topic", type_label: "Content type", tags_label: "Tags", no_tags: "No tags yet", tag_placeholder: "Add a tag and press Enter", add_tag: "Add", remove_tag: "Remove tag {{tag}}", summary_label: "Summary (English)", next_steps: "Next steps", open_editor: "Open in Content Editor", queue_kb: "Send to Ask Donny KB", kb_queued: "Queued for Ask Donny KB", kb_queued_success: "Draft sent to Ask Donny KB ingestion queue.", kb_queued_failed: "Couldn’t queue for KB", save_failed: "Couldn’t save edits", import_another: "Import another document", tip_title: "Tip", tip_body: "PDFs with selectable text work best. Scanned/image-only PDFs require OCR — we’ll add that pipeline in a future phase." },
   kb: { eyebrow: "— Ask Donny Knowledge Base", title: "Knowledge Base", subtitle: "Ingest your imported content into the Ask Donny knowledge base. Donny will retrieve and cite the most relevant DONiTSH!T library passages, and clearly note when it’s giving general guidance instead.", refresh: "Refresh", stat_chunks: "Total chunks", stat_docs: "Documents", stat_queued: "Queued for ingest", stat_store: "Vector store", ingest_title: "{{n}} documents queued for KB ingestion", ingest_help: "Each document will be chunked on heading boundaries, embedded via OpenAI text-embedding-3-small, and stored so Donny can cite them in real time.", ingest_btn: "Ingest queue", ingest_in_progress: "Ingesting…", ingest_done: "Ingested {{ok}} / {{total}} queued documents", ingest_some_failed: "{{n}} items failed — see audit log.", ingest_failed: "Ingest queue failed", by_topic: "Chunks by topic", by_locale: "Chunks by locale", no_topics: "No topics yet — ingest some content first.", no_locales: "No locales yet.", search_title: "Debug retrieval", search_help: "Test what Donny will retrieve for a given question. Useful for quality-checking after a fresh ingest.", search_placeholder: "Try: 'How do I improve my sleep with a morning routine?'", search_btn: "Search", search_empty: "Enter a query to search", search_failed: "Search failed", search_no_results: "No matching chunks found.", any_topic: "Any topic", any_locale: "Any locale", top_k: "Top {{n}}", stats_failed: "Couldn’t load KB stats" },
   biz: { eyebrow: "— Marketplace", title: "Business Listings", subtitle: "Review submissions, approve listings to publish them on the public marketplace, and manage Founding Partner badges.", refresh: "Refresh", tab_pending: "Pending", tab_active: "Active", tab_hidden: "Hidden", tab_expired: "Expired", tab_rejected: "Rejected", search_placeholder: "Search by name, category, city, country, email…", result: "result", results: "results", load_failed: "Couldn’t load businesses", action_done: "Done", action_failed: "Action failed", view: "View", approve: "Approve", reject: "Reject", hide: "Hide", expire: "Expire", reactivate: "Reactivate", fp_badge: "Founding Partner", fp_add: "Mark as Founding", fp_remove: "Unmark Founding", confirm_approve: "Approve and publish “{{name}}” to the marketplace?", confirm_expire: "Expire “{{name}}”?", approved_toast: "{{name}} is now live", expired_toast: "{{name}} expired", reactivated_toast: "{{name}} reactivated", rejected_toast: "{{name}} rejected", hidden_toast: "{{name}} hidden", fp_added_toast: "{{name}} marked as Founding Partner", fp_removed_toast: "Removed Founding Partner badge from {{name}}", reject_title: "Reject listing", reject_help: "The owner will receive an email with your reason. Be specific so they can fix it and resubmit.", reject_reason_label: "Reason (sent to owner)", reject_reason_placeholder: "e.g., Description is too short — please add details about your services and qualifications.", reject_reason_required: "Please give a reason so the owner can act on it.", reject_confirm_btn: "Reject & notify owner", hide_title: "Hide listing", hide_help: "The listing will be removed from the public marketplace but kept on file. You can reactivate it at any time. The owner is not notified.", hide_reason_label: "Internal note (optional)", hide_confirm_btn: "Hide listing", empty_title: "No listings in this tab", empty_pending_sub: "When a business submits a listing, it will appear here for review.", empty_other_sub: "Submit a listing from the marketplace, or change tabs to see existing ones.", owner_prefix: "Owner", detail_tagline: "Tagline", detail_description: "Description", detail_category: "Category", detail_location: "Location", detail_contact_email: "Contact email", detail_contact_phone: "Contact phone", detail_website: "Website", detail_owner_email: "Owner email", detail_services: "Services", detail_doshas: "Dosha alignment", detail_reject_reason: "Rejection reason sent to owner" }
  },

  // ----------------------------------------------------------------- ecosystem (shared deep content)
  ecosystem: {
    pillars_eyebrow: "— What we cover",
    pillars_title: "Topics in this section",
    editorial_eyebrow: "— Editorial",
    editorial_title: "Go deeper",
    quotes_eyebrow: "— Quotes & Mantras",
    quotes_title: "Sit with this",
    reads_eyebrow: "— Curated Reads",
    reads_title: "What to read next",
    products_eyebrow: "— From the Shop",
    products_title: "Recommended products",
    products_recommended_for: "Recommended for this path",
    donny_eyebrow: "— Your AI Concierge",
    donny_title: "Ask Donny about this",
    donny_about: "Ask Donny about {{title}}",
    donny_hl: "Donny knows you're on",
    donny_default_body: "Open the chat — Donny will scope answers to this corner of the ecosystem.",
    donny_try: "Try: \"{{prompt}}\"",
    donny_meta_chip: "Page-aware · Multilingual · Educational",
    related_eyebrow: "— Continue exploring",
    related_title: "Related in the ecosystem",
    read: "Read",
    explore: "Explore",
    program: "Program",
    donny_avatar_alt: "Donny",
  },

  // ----------------------------------------------------------------- profile
  profile: {
    tools_title: "Your DONiTSH!T tools",
    tools_eyebrow: "— Tools",
    account_eyebrow: "— Account",
    saved_eyebrow: "— Saved",
    settings: "Settings",
    country: "Country",
    country_placeholder: "—",
    saved_dosha_title: "Saved Dosha Results",
    saved_dosha_empty_prefix: "No results yet.",
    take_the_test: "Take the test",
    coach_convos_title: "Coach Conversations",
    coach_convos_empty_prefix: "Nothing yet.",
    ask_donny_link: "Ask Donny",
    my_business_title: "My Business Listings",
    my_business_sub: "Manage your marketplace listings and propose Member Benefits.",
    member_benefits_title: "Member Benefits",
    member_benefits_sub: "Browse and reveal exclusive offers from partner businesses.",
    admin_title: "Admin Console",
    admin_sub: "Review, approve, feature, hide, or expire offers proposed by partner businesses.",
    saved_practitioners_title: "My Saved Practitioners",
    saved_practitioners_subtitle: "Your shortlist of marketplace pros — quickly come back when you're ready to book.",
    saved_practitioners_empty: "You haven't saved any practitioners yet. Browse the Marketplace and tap the heart icon to build your shortlist.",
  },

  // ----------------------------------------------------------------- wellness_overview (page)
  wellness_overview: {
    eyebrow: "— Wellness",
    title: "A complete wellness ecosystem",
    subtitle: "Mind, body, and rhythm — six pillars of modern wellbeing, deeply covered. Curated content, recommended practitioners, and tools that meet you where you are.",
    explore_eyebrow: "— Explore",
    explore_title: "Six pillars of wellness",
    explore_subtitle: "Each section is a deep, evolving destination — not a landing page.",
    recs_eyebrow: "— Marketplace",
    recs_title: "Practitioners across the ecosystem",
    recs_subtitle: "A small selection from our verified wellness marketplace.",
    cards: {
      mens_blurb: "Physical health, hormones, confidence, stress, lifestyle, and men's mental wellness.",
      womens_blurb: "Cycle, hormones, fertility, postpartum, menopause, confidence, and lifestyle.",
      mental_blurb: "Stress, burnout, anxiety, confidence, motivation, and emotional resilience.",
      nutrition_blurb: "Food as medicine — dosha-aware eating, energy, recovery, and lifestyle nutrition.",
      sleep_blurb: "The most underrated wellness lever — circadian hygiene and deep recovery.",
      sports_blurb: "Training, recovery, competition, mental performance, and responsible PED awareness.",
    },
  },

  // ----------------------------------------------------------------- business_overview (page)
  business_overview: {
    eyebrow: "— Business & Corporate",
    title: "Wellness, at the business and team level",
    subtitle: "From single-practitioner partners to enterprise wellness programs — the DONiTSH!T ecosystem makes it easy to find, book, and partner with the right wellness providers.",
    cta_list: "List Your Business",
    cta_browse: "Browse Marketplace",
    sections_eyebrow: "— Sections",
    sections_title: "Business & Corporate hub",
    sections_subtitle: "Five gateways into the business side of the ecosystem.",
    recs_eyebrow: "— Marketplace",
    recs_title: "Featured corporate-ready partners",
    recs_subtitle: "Vetted businesses ready to work with teams and organizations.",
    cards: {
      partners_blurb: "Vetted practitioners and brands in the DONiTSH!T ecosystem.",
      corporate_blurb: "Wellness programs that actually move the needle for teams.",
      offers_blurb: "Member-exclusive offers from partner businesses.",
      marketplace_blurb: "Full directory with search, filters, favorites, and bookings.",
      directory_blurb: "Browse by category — therapists, coaches, studios, and more.",
    },
  },

  // ----------------------------------------------------------------- article detail
  article: {
    loading: "Loading\u2026",
    not_found: "Article not found",
    hub_back: "Hub",
    min_read: "{{n}} min read",
  },

  // ----------------------------------------------------------------- app download (Phase 6.2 add-on)
  app: {
    eyebrow: "— The Mobile Ecosystem",
    title: "DONiTSH!T in your pocket",
    subtitle: "The full ecosystem — wellness, relationships, families, marketplace, and Donny — in one luxurious mobile experience.",
    platforms_eyebrow: "— iOS · Android · Apple Watch",
    h_pre: "Your wellness ecosystem,",
    h_acc: "always with you.",
    body: "From your Dosha-aligned morning ritual to a check-in with Donny on the train — the DONiTSH!T mobile app brings the ecosystem with you, in the language and rhythm that fits your life.",
    features: [
      "Daily dosha-aligned rituals",
      "Ask Donny in your pocket",
      "Personalized wellness nudges",
      "Library: articles, audio, video",
      "Private by design",
    ],
    app_store_pre: "Download on the",
    app_store: "App Store",
    google_play_pre: "Get it on",
    google_play: "Google Play",
    mockup: {
      today_eyebrow: "— Today",
      good_morning_pre: "Good morning,",
      good_morning_acc: "Vata",
      your_dosha: "Your Dosha",
      balanced_today: "Balanced rhythm today",
      today_ritual: "Today's Ritual",
      ritual_text: "4-minute breath reset, then a warm citrus tonic.",
      ask_donny_chip: "Ask Donny — what next?",
      tab_home: "Home",
      tab_hub: "Hub",
      tab_donny: "Donny",
      tab_shop: "Shop",
      tab_me: "Me",
    },
  },

  // ----------------------------------------------------------------- motivation modal
  motivation: {
    cta_hint: "Take the next step:",
  },

  // ----------------------------------------------------------------- app_cta (gateway pages, Phase 9)
  app_cta: {
    default_eyebrow: "— In the DONiTSH!T App",
    default_title: "Take it deeper in the app.",
    default_body: "The DONiTSH!T app is the interactive layer of the ecosystem — personalised, private and always with you.",
    wellness_eyebrow: "— Wellness · In the app",
    wellness_title: "Want a more personalised wellness experience?",
    wellness_body: "Your Dosha profile, daily rituals, streaks and personalised guidance live in the DONiTSH!T app — one tap away, wherever your day takes you.",
    relationships_eyebrow: "— Relationships · In the app",
    relationships_title: "Want to connect beyond the story?",
    relationships_body: "Explore Luv Connect and Couples Connect in the DONiTSH!T app — an interactive space for adult relationships, connection education and couples growth.",
    families_eyebrow: "— Families & Parenting · In the app",
    families_title: "Want interactive support for the whole family?",
    families_body: "Parenting resources, family rhythms and guided programmes are curated inside the DONiTSH!T app — meet families where they are, day to day.",
    business_eyebrow: "— Business & Corporate · In the app",
    business_title: "Want to become part of the DONiTSH!T business ecosystem?",
    business_body: "Business members, corporate wellness partners and workshop hosts get their own space inside the DONiTSH!T app — visibility, offers and connection with the community.",
    shop_eyebrow: "— The Shop · In your pocket",
    shop_title: "Want DONiTSH!T in your pocket?",
    shop_body: "Browse, buy and re-order from the app. Everything you'd expect from your favourite editorial shop — with your DONiTSH!T rituals and Ask Donny alongside.",
    tagline: "DONiTSH!T in your pocket — Wellness, Relationships, Family, Business & Shop.",
    learn_more: "Learn more",
  },

  // ----------------------------------------------------------------- business_journey (Phase 10)
  business_journey: {
    eyebrow: "— Your business, in our ecosystem",
    title: "A clear pathway from discovery to reach.",
    subtitle:
      "How independent practitioners, wellness brands and corporate partners join, list and grow with DONiTSH!T.",
    step_label: "Step {{n}}",
    join_title: "1. Become a member",
    join_body:
      "Create your DONiTSH!T account and let us know you're representing a business. Membership unlocks the marketplace, offers and ecosystem visibility.",
    join_cta: "Create an account",
    profile_title: "2. Create your business profile",
    profile_body:
      "Submit your business — services, languages, locations and imagery. Our team reviews every listing to keep the marketplace curated and premium.",
    profile_cta: "Submit business profile",
    advertise_title: "3. Advertise inside the ecosystem",
    advertise_body:
      "Featured placements, member offers and workshop promotions land inside the DONiTSH!T ecosystem. Join the waitlist below to be first in line when advertising opens.",
    advertise_cta: "Join the waitlist",
    advertise_sent:
      "You're on the list — we'll be in touch when advertising opens.",
    reach_title: "4. Reach DONiTSH!T users",
    reach_body:
      "Members discover your business through curated recommendations, marketplace search and in-app suggestions — grounded in the DONiTSH!T Dosha framework.",
    reach_cta: "See how it looks today",
  },

  // ----------------------------------------------------------------- errors (shared)
  errors: {
    generic: "Something went wrong. Please try again.",
    required_fields: "Please fill all required fields",
    network: "Network error. Please check your connection.",
    unauthorized: "Please sign in to continue.",
  },
};
export default en;
